/**
 * AI consent, recorded once for the whole onboarding session rather than per step.
 *
 * A refusal is remembered on the same terms as a grant; only new terms or a different person reopen
 * the question. Not keyed by agent type, which would bring the question back on every step.
 */
import { SnowplowTracker } from "@/helpers/snowplow";

/** localStorage key for the recorded answer. One key; the person is held inside the record. */
export const AI_CONSENT_STORAGE_KEY = "aiConsent";

/** The answer this load is working from, once given or once recognised as this hire's. */
let sessionConsent: StoredConsent | undefined;

/**
 * Terms version sent with the `AI_SCREENSHOT_AUTOMATION` category, matching `^\d+\.\d+$`.
 *
 * A major bump from ConnectVpn's 2.0: capture now spans the session and is read by an engineer.
 * ConnectVpn keeps 2.0 while it still shows the 2.0 wording.
 */
export const AI_CONSENT_TERMS_VERSION = "3.0";

/**
 * The local half of a two-part record.
 *
 * The auditable record is the backend's. This answers "may capture run now" without a round trip,
 * which is why a local copy exists at all: there is no read endpoint.
 */
export type AiConsent = {
  /** The assistant may act on this device on the new hire's behalf. */
  allowsDeviceControl: boolean;
  /** Screenshots of the assisted steps may be stored for later review. */
  allowsScreenshotStorage: boolean;
};

type StoredConsent = AiConsent & {
  /** Absent in records from a build that stamped none, treated as a mismatch. */
  termsVersion?: string;
  /** Absent in records from a build that stamped none, treated as a mismatch. */
  personId?: string;
};

/** Consent is only granted when both permissions are given. */
export const isConsentComplete = (consent: AiConsent): boolean =>
  consent.allowsDeviceControl && consent.allowsScreenshotStorage;

/**
 * The person an answer belongs to, or undefined before sign-in resolves one.
 *
 * Two callers write it and the later wins, so it can change mid-session. Consulted once a load.
 */
const consentPersonId = (): string | undefined => SnowplowTracker.userId;

/**
 * Whether an answer given now could be read back later.
 *
 * A record written before sign-in resolves is bound to nobody, so the screen would ask forever.
 */
export const canRecordAiConsent = (): boolean =>
  consentPersonId() !== undefined;

/** A record missing either permission is discarded: a partial answer is not an answer. */
const parseStoredConsent = (raw: string): StoredConsent | undefined => {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== "object" || parsed === null) {
    return undefined;
  }
  const record = parsed as Partial<StoredConsent>;
  if (
    typeof record.allowsDeviceControl !== "boolean" ||
    typeof record.allowsScreenshotStorage !== "boolean"
  ) {
    return undefined;
  }
  return {
    allowsDeviceControl: record.allowsDeviceControl,
    allowsScreenshotStorage: record.allowsScreenshotStorage,
    termsVersion:
      typeof record.termsVersion === "string" ? record.termsVersion : undefined,
    personId: typeof record.personId === "string" ? record.personId : undefined,
  };
};

/** The persisted record, or undefined when storage holds nothing usable. */
const readPersistedConsent = (): StoredConsent | undefined => {
  try {
    const raw = localStorage.getItem(AI_CONSENT_STORAGE_KEY);
    return raw ? parseStoredConsent(raw) : undefined;
  } catch {
    // Storage is unavailable, so there is no persisted record.
    return undefined;
  }
};

const permissionsOf = (stored: StoredConsent): AiConsent => ({
  allowsDeviceControl: stored.allowsDeviceControl,
  allowsScreenshotStorage: stored.allowsScreenshotStorage,
});

/** Forget the recorded answer, so the next offer asks again. */
export const clearAiConsent = (): void => {
  sessionConsent = undefined;
  try {
    localStorage.removeItem(AI_CONSENT_STORAGE_KEY);
  } catch {
    // Storage is unavailable, so there is no persisted record to forget.
  }
};

/**
 * Whether the stored record answers for this person under this build's terms.
 *
 * Laptops are reused between hires, and inheriting a grant would capture someone never asked.
 *
 * Matched against every id this load asserted, not just the latest. The hire is named by two ids --
 * the Cognito identity, then the canonical backend personId that overwrites it -- so comparing
 * against the latest alone discards a record stamped before the overwrite, and the hire is asked
 * again on a step that should already know their answer.
 */
const answersForCurrentPerson = (stored: StoredConsent): boolean =>
  stored.termsVersion === AI_CONSENT_TERMS_VERSION &&
  stored.personId !== undefined &&
  SnowplowTracker.hasAssertedPersonId(stored.personId);

/**
 * Read the recorded consent, or undefined when nothing on record answers for this hire.
 *
 * Safe during render and under StrictMode: it only memoises the answer it just recognised.
 * The identity is checked once a load, because re-checking a changing id revokes real grants.
 */
export const readAiConsent = (): AiConsent | undefined => {
  if (sessionConsent) {
    return permissionsOf(sessionConsent);
  }
  const persisted = readPersistedConsent();
  if (!persisted || !answersForCurrentPerson(persisted)) {
    return undefined;
  }
  sessionConsent = persisted;
  return permissionsOf(persisted);
};

/** Record the new hire's answer, stamped with the terms and person it was given under. */
export const writeAiConsent = (consent: AiConsent): void => {
  const record: StoredConsent = {
    ...consent,
    termsVersion: AI_CONSENT_TERMS_VERSION,
    personId: consentPersonId(),
  };
  sessionConsent = record;
  try {
    localStorage.setItem(AI_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage is full or unavailable, so the answer holds for this load only.
  }
};
